/**
 * @param {number} ms
 *
 * @returns {Promise<void>}
 */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Redact query params in provided url
 *
 * @param {string} url
 * @param {string[]} keys
 * @param {string} redactedValue
 *
 * @returns {string}
 */
export function redactUrlQueryParams(url, keys, redactedValue = "[REDACTED]") {
  return url.replace(new RegExp(`(${keys.join("|")})=[^&#]+`, "gi"), `$1=${redactedValue}`);
}

/** Vue 3's nextTick() seems to only do one queued state change at a time - this will clear the whole queue, somehow. */
export function flushVueQueue() {
  return sleep(0);
}

/**
 *
 * @param {Record<string, Record<string, string>>} texts A key -> value map of texts.
 *                                                       Each inner Record should contain a key for each supported identifier
 * @param {string} identifier
 *
 * @returns {Record<string, string>}
 */
function mapTexts(texts, identifier) {
  return Object.fromEntries(Object.entries(texts).map(([key, text]) => [key, text[identifier]]));
}

/**
 * @param {Record<string, Record<string, string>>} texts A key -> value map of texts.
 *                                                       Each inner Record should contain a key for each supported userFunction.
 * @param {"teacher"|"student"} userFunction
 *
 * @returns {Record<string, string>}
 */
export function mapFunctionSpecificTexts(texts, userFunction) {
  return mapTexts(texts, userFunction);
}

/**
 * @param {Record<string, Record<string, string>>} texts A key -> value map of texts.
 *                                                       Each inner Record should contain a key for each supported language.
 * @param {"english"|"german"} userFunction
 *
 * @returns {Record<string, string>}
 */
export function mapLanguageSpecificTexts(texts, language) {
  return mapTexts(texts, language);
}

/**
 * Formats a real full name.
 *
 * Example: Ida Ibsen -> Ida I.
 *
 * @param {string} name
 *
 * @returns {string}
 */
export const formatName = (name) => {
  if (!name) {
    return "";
  }

  const nameParts = name.split(" ");
  let nameFormatted = nameParts[0];

  if (nameParts.length > 1) {
    const firstCharOfLastName = nameParts[nameParts.length - 1].charAt(0);
    nameFormatted += ` ${firstCharOfLastName}.`;
  }

  return nameFormatted;
};

/**
 * Gives you a random number between
 * a min and max value
 *
 * @param {number} min
 * @param {number} max
 *
 * @returns {number}
 */
export const randomInRange = (min, max) => Math.random() * (max - min) + min;

/**
 * Gives you are random number up to (excl.) the max.
 *
 * @param {number} max
 *
 * @returns {number}
 */
export const randomNumber = (max) => Math.floor(Math.random() * max);
